// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.
//
// Fleet map — physical locations of the fleet's data centers.
//
// Follows the AI Summit mock's map section. Two honesty rules carried over
// from docs/design/mock-data-philosophy.md shape what this renders:
//
//  1. A site whose location we don't know is NOT dropped. Silently omitting
//     a data center from a fleet map reads as "we have no site there", which
//     is a correctness bug, not a cosmetic one — so unplaceable sites are
//     counted and named beneath the map.
//  2. Fields with no data source render as "—" inside the hover card rather
//     than being hidden, so it's visible that the field exists and isn't yet
//     wired. Capacity and Used read each site's `metrics` (from
//     useFleetMetrics, which returns nulls until the milestone-2 endpoint
//     lands). The page can hide them instead with `showMetrics={false}`
//     (fleet-overview-metrics-enabled off), so the map never shows a figure
//     the cards beside it have dropped.

import React, { useMemo, useState, useRef, useEffect } from 'react';
import type { Severity } from '../../types/alarm';
import type { SitePosture } from '../../types/sitePosture';
import { POSTURE_LABEL, worstPosture } from '../../types/sitePosture';
import { US_STATE_PATHS } from './usGeometry';
import { project, isWithinGeometry } from './projection';
import type { Point, LatLon } from './projection';
import {
  fitViewBox,
  viewBoxAttr,
  regionLabel,
  DEFAULT_ASPECT,
  DEFAULT_FRAME_WIDTH_PX,
} from './autoFit';
import { layOutLabels, MAP_PX } from './labels';
import { lookupCoordinate } from './siteCoordinates';
import type { ProvidedCoordinate } from './siteCoordinates';
import { clusterByProximity } from './cluster';
import type { Cluster } from './cluster';
import { displaySiteKey } from '../../config/siteDisplayOverrides';
import type { FleetSiteMetrics } from '../../hooks/useFleetMetrics';
import { formatMeasure } from '../../hooks/useFleetMetrics';
import './fleet-map.css';

const SEVERITIES: Severity[] = ['P1', 'P2', 'P3', 'P4'];

export interface FleetMapSite {
  siteId: string;
  siteKey: string;
  label: string;
  counts: Record<Severity, number>;
  /**
   * The site's posture, which colours its marker. Callers derive this from
   * live alarm counts on the real fleet, or script it for a demo scenario
   * (see FleetOverview's resolvePosture and types/sitePosture.ts). Defaults
   * to healthy only when truly absent.
   */
  posture?: SitePosture;
  /** A known coordinate, taking precedence over the site_key fallback table. */
  coordinate?: ProvidedCoordinate;
  /** Street line, shown under the marker in place of the site_key. */
  addressLine1?: string;
  /** Capacity / Used for the hover card; absent or null fields show "—". */
  metrics?: FleetSiteMetrics;
}

interface PlacedSite extends FleetMapSite {
  point: Point;
  place: string;
  /** The coordinate this site was placed from, kept for the region caption. */
  latLon: LatLon;
}

export interface FleetMapProps {
  sites: readonly FleetMapSite[];
  /** Drill into a site, same behaviour as clicking its card. */
  onSelectSite?: (_siteId: string) => void;
  /**
   * Alarm counts are still arriving.
   *
   * The map still renders — artwork and markers both. Site locations come
   * from the site list, which is already resolved by the time this page
   * mounts, so the geography is real and there is no reason to hide it
   * behind a placeholder and then jump the layout when it appears. Only the
   * per-site alarm data is missing, so only the surfaces that report it
   * (hover card, picker) are held back.
   */
  loading?: boolean;
  /**
   * Show the per-site capacity fields (no data source yet) in the hover card.
   * Defaults to true; the Fleet overview turns it off with its metrics flag.
   */
  showMetrics?: boolean;
}

/**
 * The rendered size of an element, so the viewBox can be fitted to the box it
 * actually occupies (fitting to anything else either distorts the drawing or
 * letterboxes it), and so markers and labels can be sized in screen pixels.
 *
 * Falls back to the DEFAULT_* constants before first measurement and wherever
 * ResizeObserver isn't available (jsdom), so rendering never depends on it.
 */
function useRenderedBox(ref: React.RefObject<HTMLElement>): { aspect: number; widthPx: number } {
  const [box, setBox] = useState({ aspect: DEFAULT_ASPECT, widthPx: DEFAULT_FRAME_WIDTH_PX });

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;

    const measure = () => {
      const { width, height } = el.getBoundingClientRect();
      if (width > 0 && height > 0) setBox({ aspect: width / height, widthPx: width });
    };
    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);

  return box;
}

export default function FleetMap({
  sites,
  onSelectSite,
  loading = false,
  showMetrics = true,
}: FleetMapProps) {
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  /** Cluster whose "which one?" list is open, if any. */
  const [pickerId, setPickerId] = useState<string | null>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const { aspect, widthPx } = useRenderedBox(frameRef);

  const { placed, unplaced } = useMemo(() => {
    const placedSites: PlacedSite[] = [];
    const unplacedSites: FleetMapSite[] = [];
    for (const site of sites) {
      const coord = lookupCoordinate(site.siteKey, site.coordinate);
      if (!coord) {
        unplacedSites.push(site);
        continue;
      }
      const point = project(coord);
      // A real coordinate can still fall outside the artwork: it covers 36
      // states, not all 50. Treat that as unplaceable rather than drawing a
      // marker on blank canvas.
      if (!isWithinGeometry(point)) {
        unplacedSites.push(site);
        continue;
      }
      placedSites.push({
        ...site,
        point,
        place: coord.place,
        latLon: { lat: coord.lat, lon: coord.lon },
      });
    }
    return { placed: placedSites, unplaced: unplacedSites };
  }, [sites]);

  const viewBox = useMemo(
    () =>
      fitViewBox(
        placed.map(p => p.point),
        aspect
      ),
    [placed, aspect]
  );
  // Captioned from the coordinates the markers were actually drawn from —
  // re-resolving by site_key here would ignore a caller-supplied coordinate
  // and label a demo (or, later, twin-placed) fleet with the wrong region.
  const caption = useMemo(() => regionLabel(placed.map(p => p.latLon)), [placed]);

  // Geometry units per rendered pixel. Marker radii and label text are
  // specified in pixels (MAP_PX) and converted through this, so they are the
  // same size on screen at every zoom level and every frame width — see
  // labels.ts for why that is not the same as scaling against the artwork.
  const unitsPerPx = viewBox.width / (widthPx || DEFAULT_FRAME_WIDTH_PX);
  const coreR = MAP_PX.coreR * unitsPerPx;
  const haloR = MAP_PX.haloR * unitsPerPx;

  // Cluster at the marker's on-screen size, so sites group exactly when
  // their markers would overlap and hide one another.
  const clusters = useMemo(() => clusterByProximity(placed, haloR * 2), [placed, haloR]);

  // Where each cluster's name goes, and whether it fits at all.
  const labels = useMemo(
    () =>
      layOutLabels(
        clusters.map(cluster => ({
          id: cluster.id,
          point: cluster.point,
          title: clusterTitle(cluster),
          subtitle: clusterSubtitle(cluster),
          priority: labelPriority(cluster),
        })),
        { view: viewBox, unitsPerPx }
      ),
    [clusters, viewBox, unitsPerPx]
  );

  // Both of these report alarm counts, so neither is meaningful until those
  // have arrived.
  const hovered = loading ? null : (clusters.find(c => c.id === hoveredId) ?? null);
  const picking = loading ? null : (clusters.find(c => c.id === pickerId) ?? null);

  // Close the picker on Escape, matching every other dismissible surface.
  useEffect(() => {
    if (!pickerId) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPickerId(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pickerId]);

  // ...and on a click anywhere else, like every other popover in the app.
  // Markers are exempt: their own click handler decides whether to toggle
  // this picker shut or swap in another cluster's.
  useEffect(() => {
    if (!pickerId) return undefined;
    const onClick = (e: MouseEvent) => {
      const target = e.target instanceof Element ? e.target : null;
      if (!target?.closest('.fleet-map-picker, .fleet-map-marker')) {
        setPickerId(null);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [pickerId]);

  /** Marker position as a percentage of the frame, for HTML overlays. */
  const pctX = (x: number) => ((x - viewBox.x) / viewBox.width) * 100;
  const pctY = (y: number) => ((y - viewBox.y) / viewBox.height) * 100;

  const activateCluster = (cluster: Cluster<PlacedSite>) => {
    if (loading) return;
    // One site here: go straight there, no list to disambiguate.
    if (cluster.members.length === 1) {
      onSelectSite?.(cluster.members[0].siteId);
      return;
    }
    setPickerId(current => (current === cluster.id ? null : cluster.id));
  };

  if (placed.length === 0) {
    return (
      <section className="fleet-map-section">
        <FleetMapHeader caption="" loading={loading} />
        <div className="fleet-map-empty" data-testid="fleet-map-empty">
          No site locations are known yet, so there is nothing to place on the map.
          {unplaced.length > 0 && (
            <>
              {' '}
              All {unplaced.length} site{unplaced.length === 1 ? '' : 's'} are listed below.
            </>
          )}
        </div>
      </section>
    );
  }

  return (
    <section className="fleet-map-section">
      <FleetMapHeader caption={caption} loading={loading} />

      <div
        className="fleet-map-frame"
        ref={frameRef}
        data-testid="fleet-map-frame"
        aria-busy={loading || undefined}
      >
        <svg
          className="fleet-map-svg"
          viewBox={viewBoxAttr(viewBox)}
          role="img"
          aria-label={`Map of ${placed.length} data center location${placed.length === 1 ? '' : 's'}`}
        >
          <g className="fleet-map-states">
            {US_STATE_PATHS.map(state => (
              <path key={state.name} d={state.d} className="fleet-map-state" />
            ))}
          </g>

          {clusters.map(cluster => {
            const isHovered = cluster.id === hoveredId;
            const count = cluster.members.length;
            const label = labels.get(cluster.id);
            const posture = clusterPosture(cluster);
            return (
              <g
                key={cluster.id}
                className={`fleet-map-marker${loading ? ' fleet-map-marker--loading' : ''}`}
                data-testid={`fleet-map-marker-${cluster.id}`}
                onMouseEnter={() => setHoveredId(cluster.id)}
                onMouseLeave={() => setHoveredId(null)}
                onClick={() => activateCluster(cluster)}
              >
                {/* Generous invisible hit area — the visible dot is small at
                    most zoom levels and would be fiddly to hover. */}
                <circle
                  cx={cluster.point.x}
                  cy={cluster.point.y}
                  r={haloR * 1.4}
                  fill="transparent"
                />
                <circle
                  cx={cluster.point.x}
                  cy={cluster.point.y}
                  r={haloR}
                  className={`fleet-map-marker-halo fleet-map-marker-halo--${posture}`}
                  opacity={isHovered ? 0.4 : 0.22}
                />
                <circle
                  cx={cluster.point.x}
                  cy={cluster.point.y}
                  r={coreR}
                  className={`fleet-map-marker-core fleet-map-marker-core--${posture}`}
                />
                {/* A cluster carries its count, so it is obvious the dot is
                    more than one data center and worth clicking. */}
                {count > 1 && (
                  <text
                    x={cluster.point.x}
                    y={cluster.point.y + MAP_PX.count * 0.36 * unitsPerPx}
                    className="fleet-map-marker-count"
                    style={{ fontSize: `${MAP_PX.count * unitsPerPx}px` }}
                  >
                    {count}
                  </text>
                )}
                {/* A label culled for colliding with a busier marker's is
                    not lost: hovering the marker names the site (and every
                    site is in the cards below the map). Drawing it anyway
                    would just put two names on top of each other, which is
                    the clutter the cull exists to remove. */}
                {label?.visible && (
                  <g>
                    {/* text-anchor comes through `style`, not the SVG
                        attribute: the stylesheet sets it too, and a
                        stylesheet rule beats a presentation attribute. */}
                    <text
                      x={label.x}
                      y={label.titleY}
                      className="fleet-map-marker-label"
                      style={{
                        fontSize: `${MAP_PX.label * unitsPerPx}px`,
                        textAnchor: label.anchor,
                      }}
                    >
                      {clusterTitle(cluster)}
                    </text>
                    <text
                      x={label.x}
                      y={label.subtitleY}
                      className="fleet-map-marker-key"
                      style={{ fontSize: `${MAP_PX.key * unitsPerPx}px`, textAnchor: label.anchor }}
                    >
                      {clusterSubtitle(cluster)}
                    </text>
                  </g>
                )}
              </g>
            );
          })}
        </svg>

        {/* The picker replaces the hover card — showing both at one point
            would stack two panels on the same marker. */}
        {hovered && !picking && (
          <HoverCard
            cluster={hovered}
            showMetrics={showMetrics}
            // Positioned as a percentage of the frame so it tracks the
            // marker through any zoom level without a second projection.
            leftPct={pctX(hovered.point.x)}
            topPct={pctY(hovered.point.y)}
          />
        )}

        {picking && (
          <SitePicker
            cluster={picking}
            leftPct={pctX(picking.point.x)}
            topPct={pctY(picking.point.y)}
            onSelect={siteId => {
              setPickerId(null);
              onSelectSite?.(siteId);
            }}
            onDismiss={() => setPickerId(null)}
          />
        )}
      </div>

      {unplaced.length > 0 && (
        <p className="fleet-map-unplaced" data-testid="fleet-map-unplaced">
          {unplaced.length} site{unplaced.length === 1 ? '' : 's'} not shown on the map (no known
          location): {unplaced.map(s => s.label).join(', ')}
        </p>
      )}
    </section>
  );
}

/** Posture's contribution to which label survives a collision. */
const LABEL_PRIORITY_BY_POSTURE: Record<SitePosture, number> = {
  'at-risk': 2,
  healthy: 1,
  planned: 0,
};

/**
 * Which labels win the space when two collide (labels.ts culls the loser).
 *
 * Cluster size first — a marker standing for three data centers is worth more
 * of the frame than one standing for one — and posture as the tie-break,
 * because if only one of two neighbouring names can be drawn, it should be
 * the site that needs attention rather than the one that is not built yet.
 * Scaled so posture can never outrank a larger cluster.
 */
function labelPriority(cluster: Cluster<PlacedSite>): number {
  return cluster.members.length * 3 + LABEL_PRIORITY_BY_POSTURE[clusterPosture(cluster)];
}

/**
 * The posture a marker is drawn in: the worst of the sites behind it, so one
 * failing data center in a cluster is not hidden by the healthy ones.
 */
function clusterPosture(cluster: Cluster<PlacedSite>): SitePosture {
  return worstPosture(cluster.members.map(m => m.posture ?? 'healthy'));
}

/** A cluster's headline: the site's name, or how many share the location. */
function clusterTitle(cluster: Cluster<PlacedSite>): string {
  return cluster.members.length > 1
    ? `${cluster.members.length} data centers`
    : cluster.members[0].label;
}

/** The line under it: a cluster's place, or a single site's address, else its key. */
function clusterSubtitle(cluster: Cluster<PlacedSite>): string {
  if (cluster.members.length > 1) return cluster.members[0].place;
  return cluster.members[0].addressLine1 || cluster.members[0].siteKey;
}

function FleetMapHeader({ caption, loading }: { caption: string; loading?: boolean }) {
  return (
    <div className="fleet-map-head">
      <h2 className="fleet-map-title">Fleet map</h2>
      <div className="fleet-map-legend">
        {/* Only the two postures the real fleet can show (FleetOverview's
            resolvePosture derives them from live P1 counts). "Planned" is
            still drawable — a /fleet/demo scenario scripts it — but nothing
            real reports it, so it is not offered as a key (ADO 34854). */}
        <span className="fleet-map-legend-item">
          <span className="fleet-map-legend-dot fleet-map-legend-dot--ok" />
          Healthy
        </span>
        <span className="fleet-map-legend-item">
          <span className="fleet-map-legend-dot fleet-map-legend-dot--risk" />
          At risk
        </span>
        {loading ? (
          <span className="fleet-map-region fleet-map-region--loading">Loading…</span>
        ) : (
          caption && <span className="fleet-map-region">{caption}</span>
        )}
      </div>
    </div>
  );
}

function SevBadges({ counts }: { counts: Record<Severity, number> }) {
  return (
    <div className="fleet-map-tip-sevs">
      {SEVERITIES.map(sev => (
        <span key={sev} className={`hs-sev hs-sev-${sev}`}>
          <span className="dot" />
          {sev} · {counts[sev]}
        </span>
      ))}
    </div>
  );
}

function HoverCard({
  cluster,
  leftPct,
  topPct,
  showMetrics,
}: {
  cluster: Cluster<PlacedSite>;
  leftPct: number;
  topPct: number;
  showMetrics: boolean;
}) {
  const site = cluster.members[0];
  const isCluster = cluster.members.length > 1;

  return (
    <div
      className="fleet-map-tip"
      data-testid="fleet-map-tip"
      role="tooltip"
      style={{ left: `${leftPct}%`, top: `${topPct}%` }}
    >
      {isCluster ? (
        // Name every site in the cluster. Summarising as "3 data centers"
        // alone would hide which ones, which is the thing the reader needs
        // in order to decide whether to open the list.
        <>
          <div className="fleet-map-tip-head">
            <span className="fleet-map-tip-name">{cluster.members.length} data centers</span>
          </div>
          {site.place && <div className="fleet-map-tip-place">{site.place}</div>}
          <ul className="fleet-map-tip-list">
            {cluster.members.map(member => (
              <li key={member.siteId}>{member.label}</li>
            ))}
          </ul>
          <div className="fleet-map-tip-hint">Click to choose one</div>
        </>
      ) : (
        <>
          <div className="fleet-map-tip-head">
            <span className="fleet-map-tip-name">{site.label}</span>
            {/* Scripted on a demo scenario, always "Healthy" on the real
                fleet — same reason as the site cards. */}
            <span className="fleet-map-tip-posture">
              {POSTURE_LABEL[site.posture ?? 'healthy']}
            </span>
          </div>
          {site.place && <div className="fleet-map-tip-place">{site.place}</div>}

          {/* Shown as "—" rather than hidden when a figure is null: the
              field is real, the source isn't wired yet. The page turns this
              off with its metrics flag so the map never shows what the site
              cards have dropped. */}
          {showMetrics && (
            <>
              <div className="fleet-map-tip-row">
                <span>Capacity</span>
                <span className="fleet-map-tip-value" data-testid="fleet-map-tip-capacity">
                  {formatMeasure(site.metrics?.capacity ?? null)}
                </span>
              </div>
              <div className="fleet-map-tip-row">
                <span>Used</span>
                <span className="fleet-map-tip-value" data-testid="fleet-map-tip-used">
                  {formatMeasure(site.metrics?.used ?? null)}
                </span>
              </div>
            </>
          )}

          <SevBadges counts={site.counts} />
        </>
      )}
    </div>
  );
}

/**
 * "Which one?" list for a marker covering more than one data center.
 *
 * Without this, the site drawn underneath is unreachable from the map — you
 * cannot click what you cannot see. Each entry carries the same identifying
 * detail as a site card (name, key, alarm counts) so the choice can be made
 * here rather than by trial and error.
 */
function SitePicker({
  cluster,
  leftPct,
  topPct,
  onSelect,
  onDismiss,
}: {
  cluster: Cluster<PlacedSite>;
  leftPct: number;
  topPct: number;
  onSelect: (_siteId: string) => void;
  onDismiss: () => void;
}) {
  return (
    <div
      className="fleet-map-picker"
      data-testid="fleet-map-picker"
      style={{ left: `${leftPct}%`, top: `${topPct}%` }}
      role="dialog"
      aria-label={`Choose a data center at ${cluster.members[0].place || 'this location'}`}
    >
      <div className="fleet-map-picker-head">
        <span>
          {cluster.members.length} data centers
          {cluster.members[0].place ? ` · ${cluster.members[0].place}` : ''}
        </span>
        <button
          type="button"
          className="fleet-map-picker-close"
          onClick={onDismiss}
          aria-label="Close"
        >
          ×
        </button>
      </div>
      <ul className="fleet-map-picker-list">
        {cluster.members.map(member => (
          <li key={member.siteId}>
            <button
              type="button"
              className="fleet-map-picker-item"
              data-testid={`fleet-map-picker-item-${member.siteId}`}
              onClick={() => onSelect(member.siteId)}
            >
              <span className="fleet-map-picker-name">{member.label}</span>
              <span className="fleet-map-picker-key">{displaySiteKey(member.siteKey)}</span>
              <SevBadges counts={member.counts} />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
